import {
    createRouter,
    createWebHistory,
    Router,
    RouteRecordRaw
} from 'vue-router';

import * as tmi from "@/constants";

// Services
import routeManager from "@/router/route-manager";
import { createAuthGuard } from "@/router/auth-guard";
import { mainScrollBehavior, recordMainScroll } from "@/router/main-scroll";
import { focusRouteChange } from "@/router/route-focus";
import { reportNavigationError } from "@/router/navigation-errors";
import { tickerTapeClickPath } from "@/composables/market-data/ticker-tape-symbols";

// Plugins
import pinia from "@/plugins/PiniaPlugin";

// Constants
import {
    courseKey,
    discordKey,
    discordRegistrationKey,
    etfListingKey,
    majorIndexBreadthKey,
    notFoundKey,
    nyseBpiKey,
    portfolioBuilderKey,
    positionSizeCalculatorKey,
    relativeStrengthMatrixKey,
    sectorAlertsKey,
    tradeAlertSearchKey
} from "@/types/products/product-definitions";

// Pinia Stores

// User-scoped storage purge (R7)

// import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
// const logger = createDefaultLogger();
// CachedComponentWrapper stays static on purpose: it is a 13-line passthrough that renders
// `<component :is="route.meta.category" />`, fronting components that are ALREADY async. It backs
// 15 routes, so making it lazy would add a serial round trip to each of them in exchange for
// essentially no bytes.
import CachedComponentWrapper from "@/pages/CachedComponentWrapper.vue";
import { sectorDetailsKey } from "@/plugins/SectorToolsPlugin";

import { useCoursesStore } from "@/stores/Courses";
import { NotFound } from "@tmi/types/api/responses";

export const myArticlesKey: string = 'my-articles';
export const myWebinarsKey: string = 'my-webinars';

// Route components are lazy by default. These seven used to be static imports, which meant every
// anonymous visitor — including one landing on a single article — downloaded the signed-in
// dashboard, the sign-in and sign-up pages, the courses screen, the bonus-report search and the
// webinar search inside the entry chunk before anything could render.
const Authorization = () => import( '@/pages/Authorization.vue' );
const Registration = () => import( '@/pages/Registration.vue' );
const Forbidden403 = () => import( '@/pages/Forbidden403.vue' );
const Home = () => import( '@/pages/Home.vue' );
const BonusReportsSearch = () => import( '@/components/search/BonusReportSearch.vue' );
const Courses = () => import( '@/components/courses/Courses.vue' );
const WebinarsSearch = () => import( '@/components/search/WebinarSearch.vue' );

// --- PRODUCTS
const Products = () => import( '@/pages/Product.vue' );

const AllBPIs = () => import( '@/pages/Tools/AllBPIs.vue' );
const StockReplacementStrategyCalculator = () => import( '@/pages/Tools/StockReplacementStrategyCalculator.vue' );
const Article = () => import( '@/pages/Article.vue' );
const TradeAlert = () => import( '@/pages/TradeAlert.vue' );
const Author = () => import( '@/pages/Author.vue' );
const SectorDetailsPage = () => import( '@/pages/SectorDetailsPage.vue' );

// Components used within the Product Pages
export const SectorsAsync = () => import( '@/components/sectors/SectorsList.vue' );
// export const SrsCalculatorAsync = () => import( '@/components/calculators/StockReplacementStrategyCalculator.vue' );

const CalculationHelp = () => import( '@/pages/CalculationHelp.vue' );

const Login = () => import( '@/components/Login.vue' );
const NewPassword = () => import( '@/pages/ResetPassword/NewPassword.vue' );
const Profile = () => import( '@/pages/Profile.vue' );
const MyProfile = () => import( '@/components/my/MyProfile.vue' );
const ResetPassword = () => import( '@/pages/ResetPassword/index.vue' );
const SimpleContentfulPage = () => import( '@/pages/SimpleContentfulPage.vue' );
const SectorEdge = () => import( '@/pages/SectorEdge.vue' );
const SecurityDetails = () => import( '@/components/securities/SecurityDetails.vue' );
const Webinar = () => import( '@/pages/Webinar.vue' );

const ArticleSearch = () => import( "@/components/search/ArticleSearch.vue" );

const AppToolsPage = () => import( '@/pages/Support/AppTools.vue' );

// meta.title names the page in the tab title (useAppHead adds the site name and the badge); a page whose name comes from
// its data (an article, a ticker) overrides it with usePageSeo. meta.indexEligible also drives the robots meta and the
// canonical link: a route without it is noindex.
const routes: RouteRecordRaw[] = [
    // -------- Default landing page & alias "/" --------
    {
        path: tmi.HomePath,
        name: tmi.HomeName,
        alias: tmi.DashboardPath,
        meta: { public: true, indexEligible: true },
        component: Home
    },
    // -------- Unauthenticated/Public free content routes (media/sector-edge/sectoredge) --------
    {
        path: tmi.SectorEdgePath,
        name: tmi.SectorEdgeSlug,
        meta: { public: true, indexEligible: true, title: 'Sector Edge' },
        component: SectorEdge
    },
    // -------- Redirector for /sector-edge => /sectoredge --------
    {
        path: tmi.SectorDashEdgePath,
        redirect: tmi.SectorEdgePath
    },
    // -------- Redirector for /media => /sectoredge --------
    {
        path: tmi.MediaPath,
        redirect: tmi.SectorEdgePath
    },
    // -------- Unauthenticated/Public WTF Email Confirmation bullshit route --------
    {
        path: tmi.TestimonialPath,
        meta: { public: true },
        props: {
            slug: tmi.TestimonialSlug,
        },
        component: SimpleContentfulPage
    },
    // -------- Unauthenticated/Public WTF stand-alone testimonial bullshit route --------
    {
        path: tmi.EmailConfirmationPath,
        meta: { public: true },
        props: {
            slug: tmi.EmailConfirmationSlug,
        },
        component: SimpleContentfulPage
    },
    // -------- Unauthenticated/Public informational routes --------
    {
        path: tmi.AboutUsPath,
        redirect: tmi.HomePath,
    },
    {
        path: tmi.ContactUsPath,
        name: tmi.ContactUsDisplayText,
        meta: { public: true, indexEligible: true, title: 'Contact Us' },
        props: {
            slug: tmi.ContactUsSlug,
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.PrivacyPolicyPath,
        name: tmi.PrivacyPolicySlug,
        meta: { public: true, indexEligible: true, title: 'Privacy Policy' },
        props: {
            slug: tmi.PrivacyPolicySlug,
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.RefundPolicyPath,
        name: tmi.RefundPolicySlug,
        meta: { public: true, indexEligible: true, title: 'Refund Policy' },
        props: {
            slug: tmi.RefundPolicySlug
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.TermsOfUsePolicyPath,
        name: tmi.TermsOfUsePolicySlug,
        meta: { public: true, indexEligible: true, title: 'Terms of Use' },
        props: {
            slug: tmi.TermsOfUsePolicySlug
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.SmsTextPolicyPath,
        name: tmi.SmsTextPolicySlug,
        meta: { public: true, indexEligible: true, title: 'SMS Text Policy' },
        props: {
            slug: tmi.SmsTextPolicySlug
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.WhitelistInstructionsPath,
        name: tmi.WhitelistInstructionsSlug,
        meta: { public: true, indexEligible: true, title: 'Whitelist Instructions' },
        props: {
            slug: tmi.WhitelistInstructionsSlug
        },
        component: SimpleContentfulPage
    },
    // -------- Authentication routes --------
    // Not indexEligible (rank 105): a sign-in form has no search value, robots.txt already disallows these, and the
    // flag now drives the robots meta (composables/useAppHead.ts).
    {
        path: tmi.SignInPath,
        meta: { public: true, title: 'Sign In' },
        component: Authorization,
        children: [
            {
                path: tmi.SignInPath,
                name: tmi.SignInName,
                meta: { public: true, title: 'Sign In' },
                components: {
                    loginSection: Login
                }
            },
            {
                path: tmi.SignInAsUserPath,
                name: tmi.SignInAsUserName,
                meta: { public: true, title: 'Sign In' },
                components: {
                    loginSection: Login
                }
            }
        ]
    },
    {
        path: tmi.SignUpPath,
        name: tmi.SignUpName,
        meta: { public: true, title: 'Sign Up' },
        component: Registration,
    },
    {
        path: tmi.ResetPasswordPath,
        name: tmi.ResetPasswordName,
        meta: { public: true, title: 'Reset Password' },
        component: ResetPassword
    },
    {
        path: tmi.NewPasswordPath,
        name: tmi.NewPasswordName,
        meta: { public: true, title: 'New Password' },
        component: NewPassword
    },
    // -------- Product routing --------
    {
        path: tmi.ProductsPath,
        name: tmi.ProductsName,
        alias: tmi.ProductsAdvancedAliasPath,
        component: Products,
        meta: { public: true, indexEligible: true, title: 'Products' }
    },
    // -------- Tools routes --------
    {
        path: tmi.NyseBpiPath,
        name: tmi.NyseBpiName,
        meta: {
            public: true,
            // In the sitemap, so it must not carry a noindex
            indexEligible: true,
            title: 'NYSE Bullish Percent Index',
            category: nyseBpiKey
        },
        component: CachedComponentWrapper
    },
    // -------- Content-based routes --------
    {
        path: tmi.ArticleSearchPath,
        name: tmi.ArticleSearchName,
        // In the sitemap, so it must not carry a noindex
        meta: { public: true, indexEligible: true, title: 'Articles' },
        component: ArticleSearch
    },
    {
        path: tmi.BonusReportsSearchPath,
        name: tmi.BonusReportsSearchName,
        meta: {
            public: false,
            permission: tmi.BonusReportsPermissionKey,
            title: 'Bonus Reports'
        },
        component: BonusReportsSearch
    },
    {
        path: tmi.AuthorBioPath,
        name: tmi.AuthorBioName,
        meta: { public: true, indexEligible: true, title: 'Author' },
        component: Author
    },
    // -------- Alert-based routes --------
    {
        path: tmi.ShadowSpikePath,
        name: tmi.ShadowSpikeName,
        meta: {
            public: false,
            permission: tmi.ShadowScannerPermissionKey,
            shortCode: tmi.ShadowScannerShortCode,
            title: 'Shadow Spike Scanner'
        },
        component: () => import( "@/pages/ShadowSpikeScanner.vue" )
    },
    {
        path: tmi.TradeAlertsPath,
        name: tmi.TradeAlertsName,
        alias: tmi.LearningTradeAlertsAliasPath,
        meta: {
            public: false,
            permission: tmi.ProductTradeAlertsPermissionKey,
            title: 'Trade Alerts',
            category: tradeAlertSearchKey
        },
        component: CachedComponentWrapper
    },
    {
        path: tmi.TradeAlertPath,
        name: tmi.TradeAlertName,
        alias: tmi.LearningTradeAlertAliasPath,
        meta: {
            public: false,
            permission: tmi.ProductTradeAlertsPermissionKey,
            title: 'Trade Alert',
            // UX roadmap #19: App.vue renders this page once the auth guard passes, not after the whole data boot.
            rendersBeforeBoot: true
        },
        component: TradeAlert
    },
    // -------- Article-based routes --------
    {
        path: tmi.ArticlePath,
        name: tmi.ArticleName,
        meta: { public: true, indexEligible: true, title: 'Article' },
        component: Article
    },
    // -------- Webinar-based routes --------
    {
        path: tmi.WebinarSearchPath,
        name: tmi.WebinarsName,
        meta: { public: true, indexEligible: true, title: 'Webinars' },
        component: WebinarsSearch
    },
    { path: tmi.WebinarAllAliasPath, redirect: tmi.WebinarSearchPath },
    { path: tmi.WebinarCompletedAliasPath, redirect: { path: tmi.WebinarSearchPath, query: { view: 'past' } } },
    { path: tmi.WebinarMainAliasPath, redirect: tmi.WebinarSearchPath },
    { path: tmi.WebinarUpcomingAliasPath, redirect: { path: tmi.WebinarSearchPath, query: { view: 'upcoming' } } },
    {
        path: tmi.WebinarPath,
        name: tmi.WebinarName,
        meta: { public: true, indexEligible: true, title: 'Webinar' },
        component: Webinar
    },
    // --------  Lesson-based routes --------
    {
        path: tmi.CoursesPath,
        name: tmi.CoursesName,
        meta: {
            public: false,
            permission: tmi.CoursePermissionsKey,
            title: 'Courses'
        },
        beforeEnter: () => {
            useCoursesStore( pinia ).initializeAsync().then();
        },
        component: Courses
    },
    {
        path: tmi.CoursePath,
        name: tmi.CourseName,
        meta: {
            public: false,
            permission: tmi.CoursePermissionsKey,
            title: 'Course',
            category: courseKey
        },
        beforeEnter: ( to ) => {
            const coursesStore = useCoursesStore( pinia );
            coursesStore.initializeAsync().then();

            if ( typeof to.params.courseSlug !== 'string' ) {
                // TODO: update the UI to display the lack of authorization
                // reject the navigation
                throw new NotFound();
            }

            coursesStore.setSelectCourseByCourseSlug( `${ to.params.courseSlug }` );
        },
        component: CachedComponentWrapper
    },
    {
        path: tmi.ForbiddenPath,
        name: tmi.ForbiddenName,
        meta: { public: true, title: 'Access Required' },
        component: Forbidden403
    },
    {
        path: tmi.SectorTreePath,
        name: tmi.SectorTreeSlug,
        meta: { public: false, permission: tmi.SectorsPermissionKey, title: 'Sector Tree' },
        props: {
            slug: tmi.SectorTreeSlug,
        },
        component: SimpleContentfulPage
    },
    {
        path: tmi.SectorsPath,
        name: tmi.SectorsName,
        meta: {
            public: false,
            permission: tmi.SectorsPermissionKey,
            title: 'Sectors'
        },
        component: SectorsAsync
    },
    {
        path: tmi.EtfListingPath,
        name: tmi.EtfListingName,
        meta: {
            public: false,
            permission: tmi.EtfListingPermissionsKey,
            title: 'ETFs',
            category: etfListingKey
        },
        component: CachedComponentWrapper,
    },
    {
        path: tmi.RelativeStrengthMatrixPath,
        name: tmi.RelativeStrengthMatrixName,
        alias: tmi.MatrixHubRelativeStrengthPath,
        meta: {
            public: false,
            permission: tmi.RelativeStrengthMatrixPermissionKey,
            title: 'Relative Strength Matrix',
            category: relativeStrengthMatrixKey
        },

        component: CachedComponentWrapper
    },
    {
        path: tmi.SectorDetailsPath,
        name: tmi.SectorDetailsName,
        meta: {
            public: false,
            permission: tmi.SectorsPermissionKey,
            title: 'Sectors',
            category: sectorDetailsKey
        },
        component: SectorsAsync,
    },
    // The sector's own page: the flyout's content at full width, opened in a new window from the flyout's header
    {
        path: tmi.SectorDetailsPagePath,
        name: tmi.SectorDetailsPageName,
        meta: {
            public: false,
            permission: tmi.SectorsPermissionKey,
            title: 'Sector Details'
        },
        component: SectorDetailsPage
    },
    {
        path: tmi.SecurityDetailsPath,
        meta: {
            public: false,
            permission: tmi.StockPermissionsKey,
            title: 'Securities'
        },
        component: SecurityDetails
    },
    {
        path: tmi.EtfDetailsAliasPath,
        redirect: to => {
            return { path: `${ tmi.SecurityDetailsBasePath }/${ to.params.ticker }` }
        }
    },
    {
        path: tmi.StockDetailsAliasPath,
        redirect: to => {
            return { path: `${ tmi.SecurityDetailsBasePath }/${ to.params.ticker }` }
        }
    },
    // -------- TradingView widget clicks (ticker tape, stock heatmap): each symbol links to /securities?tvwidgetsymbol=<symbol> --------
    {
        path: tmi.SecurityDetailsBasePath,
        // An empty query, or the redirect would carry ?tvwidgetsymbol=… along to the destination
        redirect: to => ( { path: tickerTapeClickPath( to.query.tvwidgetsymbol ), query: {} } )
    },
    /* Private routes */
    {
        path: tmi.ProfilePath,
        meta: { public: false, permission: tmi.ProfilePermissionKey },
        component: Profile,
        children: [
            {
                path: '',
                name: tmi.ProfileName,
                props: { activeTab: tmi.Articles },
                meta: { public: false, permission: tmi.ProfilePermissionKey, title: 'My Profile' },
                component: MyProfile
            },
            {
                path: tmi.MyLibraryPath,
                name: tmi.MyLibraryName,
                props: {
                    activeTab: tmi.Articles
                },
                meta: {
                    public: false,
                    permission: tmi.ProfilePermissionKey,
                    title: 'My Articles',
                    category: myArticlesKey
                },
                component: CachedComponentWrapper
            },
            {
                path: tmi.MyWebinarsPath,
                name: tmi.MyWebinarsName,
                props: {
                    activeTab: tmi.Webinars
                },
                meta: {
                    public: false,
                    permission: tmi.ProfilePermissionKey,
                    title: 'My Webinars',
                    category: myWebinarsKey
                },
                component: CachedComponentWrapper
            }
        ]
    },
    {
        path: tmi.MajorIndexBreadthPath,
        name: tmi.MajorIndexBreadthName,
        meta: {
            public: false,
            permission: tmi.MajorIndexBreadthPermissionsKey,
            title: 'Major Index Breadth',
            category: majorIndexBreadthKey
        },
        component: CachedComponentWrapper
    },
    {
        path: '/tools/options-calculator/calculation-help',
        name: 'calculation-help',
        meta: { public: true, title: 'Calculation Help' },
        component: CalculationHelp
    },
    {
        path: routeManager.spreadCalculatorPath(),
        name: tmi.SpreadCalculatorKey,
        meta: {
            public: false,
            permission: tmi.SpreadCalculatorsPermissionKey,
            title: 'Spread Calculator',
            category: tmi.SpreadCalculatorKey
        },
        component: CachedComponentWrapper
    },
    {
        path: '/tools/position-size-calculator',
        name: 'PositionSizeCalculator',
        meta: {
            public: false,
            permission: tmi.PositionSizeCalculatorPermissionKey,
            title: 'Position Size Calculator',
            category: positionSizeCalculatorKey
        },
        component: CachedComponentWrapper
    },
    {
        path: tmi.StockReplacementStrategyCalculatorPath,
        name: tmi.StockReplacementStrategyCalculatorName,
        meta: {
            public: false,
            permission: tmi.StockReplacementStrategyCalculatorPermissionKey,
            title: 'Stock Replacement Strategy Calculator'
        },
        component: StockReplacementStrategyCalculator
    },
    {
        path: tmi.SectorBullishPercentIndicesPath,
        name: tmi.SectorBullishPercentIndicesName,
        alias: tmi.AllSectorBPIsPath,
        meta: {
            public: false,
            permission: tmi.AllSectorBullishPercentIndexesPermissionKey,
            title: 'Sector Bullish Percent Indices'
        },
        component: AllBPIs
    },
    {
        path: tmi.PortfolioBuilderPath,
        alias: tmi.PortfoliosPath,
        name: tmi.PortfolioBuilderName,
        meta: {
            public: false,
            permission: tmi.PortfolioBuilderPermissionKey,
            title: 'My Portfolios',
            category: portfolioBuilderKey
        },
        component: CachedComponentWrapper
    },
    {
        path: tmi.SectorAlertsPath,
        name: tmi.SectorAlertsName,
        alias: tmi.AlertsAliasPath,
        meta: {
            public: false,
            permission: tmi.SectorAlertsPermissionKey,
            title: 'Sector Alerts',
            category: sectorAlertsKey
        },
        component: CachedComponentWrapper
    },
    {
        path: tmi.AppToolsPath,
        name: tmi.AppToolsName,
        alias: tmi.AppToolsPath,
        meta: {
            public: true,
            title: 'App Tools'
        },
        component: AppToolsPage
    },
    // -------- Discord OAuth routes --------
    {
        path: tmi.DiscordPath,
        name: tmi.DiscordName,
        meta: {
            public: false,
            permission: tmi.DashboardPermissionsKey,
            title: 'Discord',
            category: discordKey
        },
        component: CachedComponentWrapper
    },
    {
        path: tmi.DiscordCallbackPath,
        name: tmi.DiscordCallbackName,
        meta: {
            public: false,
            permission: tmi.DiscordPermissionsKey,
            title: 'Discord',
            category: discordRegistrationKey
        },
        component: CachedComponentWrapper
    },
    {
        path: "/:catchAll(.*)",
        name: notFoundKey,
        meta: {
            public: true,
            title: 'Page Not Found',
            category: notFoundKey
        },
        component: CachedComponentWrapper
    }
]

const router: Router = createRouter( {
    history: createWebHistory( import.meta.env.BASE_URL ),
    // From lg the private shell's <main> owns the scroll, not the window: main-scroll.ts saves and restores it per
    // history entry. Below lg that shell scrolls the document (UX roadmap #107), and main-scroll.ts restores
    // vue-router's saved window position there, waiting for content still loading; the public layout keeps it as is.
    scrollBehavior: mainScrollBehavior,
    routes
} );

/*
---------------------------------------------
The Full Navigation Resolution Flow
---------------------------------------------
  * Navigation triggered.
     1. Call beforeRouteLeave guards in deactivated components.
     2. Call beforeRouteUpdate guards in reused components.
     3. Call beforeEnter in route configs.
     4. Resolve async route components.
     5. Call beforeRouteEnter in activated components.
     6. Call global beforeResolve guards.
     7. Navigation is confirmed.
     8. Call global afterEach hooks.
     9. DOM updates triggered.
    10. Call callbacks passed to next in beforeRouteEnter guards with instantiated instances
*/

// Step #6 in the lifecycle flow above. The guard body lives in auth-guard.ts (tmi-6gh, LD6).
router.beforeResolve( createAuthGuard( pinia ) );

// Step #8: the URL has changed but the new page is not patched in yet, so <main> still holds the old page's offset.
router.afterEach( recordMainScroll );

// Rank 49: once the new page has rendered, focus its heading and name it in the live region (route-focus.ts).
router.afterEach( focusRouteChange );

// A thrown guard, a failed route chunk or a throwing scrollBehavior. Without a listener vue-router only logs them.
router.onError( reportNavigationError );

export default router;
